// web/src/components/enroll/ReceiverFlow.tsx — the RECEIVER join + waiting-room
// flow (wireframe 05; ADR-003 D3/D4/D7; SEAL dynamic receiver side). Steps:
//   intro → auth (OIDC stub / magic-link guest) → key choice (software; PRF/
//   hardware optional with graceful fallback) → generate keypair + publish the
//   PUBLIC key (the Worker binds + signs the attestation) → waiting room.
//
// Custody invariant (R11 / ADR-001): the ECDH decryption private key and the
// ECDSA signing private key are generated on-device, non-extractable, and never
// leave this browser. Only the public JWKs + the derived safety number travel.
// The join code grants LOBBY ENTRY ONLY — trust is conferred by the initiator's
// out-of-band admit, never by code possession.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { api } from '../../api';
import { DECRYPT_KEY_ALG, SOFTWARE_KEY_KIND } from '../../api';
import { ApiError } from '../../api';
import {
  fingerprintCodec,
  keyManager,
  signingKeyManager,
  type Fingerprint,
} from '../../crypto';
import { detectCapabilities, unsupportedMessage } from '../../capabilities';
import { Icon } from '../ui/Icon';
import { CodeBlock, FeedbackMessage } from '../ui';
import { ShieldBanner } from './ShieldBanner';

type Stage = 'intro' | 'auth' | 'keychoice' | 'generating' | 'waiting' | 'unsupported';

type KeyHold = 'software' | 'hardware';

/** What we learned about the joiner once authenticated. */
interface JoinerAuth {
  email: string;
  method: 'oidc' | 'magic-link';
  /** Guest principal id when on the magic-link path; null for OIDC stub. */
  guestPrincipalId: string | null;
}

export function ReceiverFlow(): React.JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ code?: string }>();
  const [search] = useSearchParams();

  // The join code may arrive in the path (/join/:code) or be typed on the intro.
  const codeFromPath = params.code ?? '';
  const magicToken = search.get('ml');

  const caps = useMemo(() => detectCapabilities(), []);
  const [stage, setStage] = useState<Stage>(caps.usable ? 'intro' : 'unsupported');

  // Join code entry (words + PIN). Words can preload from the path.
  const [joinWords, setJoinWords] = useState(codeFromPath);
  const [pin, setPin] = useState('');

  const [auth, setAuth] = useState<JoinerAuth | null>(null);
  const [email, setEmail] = useState('');
  const [hold, setHold] = useState<KeyHold>('software');
  const [fingerprint, setFingerprint] = useState<Fingerprint | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [genLabel, setGenLabel] = useState('Generating your key on this device…');

  const prfNote =
    caps.webAuthnPrf.level !== 'supported'
      ? 'Your security key may not support the feature Break Glass needs — it falls back to a software key, still encrypted to you alone.'
      : null;

  // ---- Auth ---------------------------------------------------------------

  // OIDC member path — reuses the AuthProvider stub (GET /me). Real OIDC drops in
  // with no call-site change (D-013 / R5).
  const signInOidc = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const me = await api.me();
      setAuth({ email: me.email, method: 'oidc', guestPrincipalId: null });
      setStage('keychoice');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in didn’t complete.');
    } finally {
      setBusy(false);
    }
  }, []);

  // Magic-link guest path (D7): consume the single-use token bound to this
  // session, yielding a session-scoped guest principal (guest:<email>).
  const consumeMagicLink = useCallback(
    async (sessionId: string, token: string) => {
      setBusy(true);
      setError(null);
      try {
        const guest = await api.verifyMagicLink(sessionId, { token });
        setAuth({ email: guest.email, method: 'magic-link', guestPrincipalId: guest.principalId });
        setStage('keychoice');
      } catch (e) {
        const msg =
          e instanceof ApiError && e.status === 410
            ? 'This link has expired or has already been used. Ask the initiator to send a new one.'
            : e instanceof Error
              ? e.message
              : 'We couldn’t verify your link.';
        setError(msg);
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const sendMagicLink = useCallback(async () => {
    // In the stubbed transport the initiator mints the link; a guest who arrives
    // without a token enters their email and is told to check for the link. We
    // surface the path honestly rather than minting a link guest-side.
    setError(
      'Ask the initiator to send you a magic link. Open it from your email to verify as a guest.',
    );
  }, []);

  // If a magic-link token rode in on the URL, resolve the code → session and
  // consume it as soon as we know the session id.
  const [resolvedSessionId, setResolvedSessionId] = useState<string | null>(null);
  useEffect(() => {
    if (!magicToken || !codeFromPath) return;
    let cancelled = false;
    // We need the session id behind the code. The join endpoint resolves it; but
    // verify is keyed by session id, so we step to auth and let the guest confirm.
    setStage('auth');
    void (async () => {
      try {
        // Resolve the session by treating the path code as words; the server maps
        // an open code → its single session (D3). We fetch via getEnrollSession
        // using the code as a best-effort id; on failure the guest types the PIN.
        const session = await api.getEnrollSession(codeFromPath).catch(() => null);
        if (cancelled) return;
        if (session) {
          setResolvedSessionId(session.id);
          await consumeMagicLink(session.id, magicToken);
        }
      } catch {
        /* fall through to manual auth */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [magicToken, codeFromPath, consumeMagicLink]);

  // ---- Key generation + publish ------------------------------------------

  const generateAndPublish = useCallback(async () => {
    if (!auth) return;
    setStage('generating');
    setBusy(true);
    setError(null);
    try {
      // 1. Non-extractable ECDH decryption keypair (ADR-001) — the key shards
      //    are sealed to. PRF/hardware wrap is optional; we fall back to software
      //    if it is unavailable (graceful, per the capability matrix).
      setGenLabel('Generating your key on this device…');
      const ecdh = await keyManager.generateKeyPair();

      // 2. Distinct ECDSA signing keypair (ADR-003 D4/D5) for attestation +
      //    possession-proof signatures. Never used for decryption (A4).
      await signingKeyManager.generateSigningKeyPair();

      // 3. Derive the safety number for the out-of-band admit check (D3).
      const fp = await fingerprintCodec.derive(ecdh.publicKey);
      setFingerprint(fp);

      // 4. Publish ONLY the public key; the Worker binds it to the principal and
      //    signs the attestation (D4). No private material crosses this call.
      setGenLabel('Publishing your public key to the lobby…');
      await api.publishKey({
        publicKeyJwk: JSON.stringify(ecdh.publicKey),
        alg: DECRYPT_KEY_ALG,
        keyKind: SOFTWARE_KEY_KIND,
      });

      // 5. Enter the lobby for this join code (JOINED). Code = lobby entry only.
      //    This is now a real, load-bearing call: if it fails, the receiver is NOT
      //    in the lobby and the initiator will never see them, so we surface the
      //    failure honestly instead of pretending they reached the waiting room.
      await api.joinSession({
        joinWords: joinWords.trim(),
        pin: pin.trim(),
        pubkeyId: ecdh.keyId as unknown as string,
        guestPrincipalId: auth.guestPrincipalId ?? undefined,
      });

      setStage('waiting');
    } catch (e) {
      // A failed join (bad code / wrong PIN / expired or full lobby) is distinct
      // from a key-gen failure: send the joiner back to the code entry to retry,
      // not to the key choice, so they don't loop generating keys against a dead
      // code. The key is already generated locally; only the lobby entry failed.
      if (e instanceof ApiError) {
        const msg =
          e.status === 404
            ? 'That join code wasn’t found. Check the words and PIN with the initiator.'
            : e.status === 401 || e.status === 403
              ? 'The PIN didn’t match this code. Check it with the initiator and try again.'
              : e.status === 410
                ? 'This lobby has closed or expired. Ask the initiator to open a new session.'
                : e.message || 'Couldn’t enter the lobby with that code.';
        setError(msg);
        setStage('intro');
      } else {
        setError(
          e instanceof Error
            ? e.message
            : 'Your browser couldn’t generate a secure key. This can happen in private windows or older browsers.',
        );
        setStage('keychoice');
      }
    } finally {
      setBusy(false);
    }
  }, [auth, joinWords, pin]);

  // ---- Render -------------------------------------------------------------

  if (stage === 'unsupported') {
    return (
      <div className="bgJoinFrame">
        <div className="bgPanel">
          <div className="bgPanelBody">
            <h1 style={{ fontSize: 'var(--size-h2)', color: 'var(--color-text)' }}>
              Break Glass needs a few things this browser doesn’t have
            </h1>
            <p style={{ color: 'var(--color-text2)' }}>{unsupportedMessage(caps)}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bgJoinFrame">
      <ShieldBanner
        lead="You’ll hold one encrypted shard. You alone can decrypt it."
        note="The server only ever sees ciphertext — never your secret."
      />

      <ol className="bgSteps" aria-label="Join progress" style={{ justifyContent: 'center' }}>
        {(['Intro', 'Verify', 'Key', 'Lobby'] as const).map((label, i) => {
          const order: Stage[] = ['intro', 'auth', 'keychoice', 'waiting'];
          const cur = order.indexOf(
            stage === 'generating' ? 'keychoice' : (stage as Stage),
          );
          return (
            <li key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span
                className={`bgStepDot ${i === cur ? 'bgStepOn' : ''} ${i < cur ? 'bgStepDone' : ''}`}
                aria-hidden="true"
              />
              <span aria-current={i === cur ? 'step' : undefined}>{label}</span>
            </li>
          );
        })}
      </ol>

      {stage === 'intro' && (
        <div className="bgPanel">
          <div className="bgPanelBody">
            <h1 style={{ fontSize: 'var(--size-h2)', color: 'var(--color-text)' }}>
              Join · Break Glass
            </h1>
            <p style={{ color: 'var(--color-text2)' }}>
              You’ve been invited to help safeguard a secret. You’ll hold one encrypted shard,
              and you alone can decrypt it.
            </p>
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              <label className="field-label" htmlFor="jc-words">Join code</label>
              <input
                id="jc-words"
                className="input"
                value={joinWords}
                onChange={(e) => setJoinWords(e.target.value)}
                placeholder="amber.cedar.river"
                style={{ fontFamily: 'var(--font-mono)' }}
              />
              <label className="field-label" htmlFor="jc-pin">PIN</label>
              <input
                id="jc-pin"
                className="input"
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                style={{ fontFamily: 'var(--font-mono)', width: '8rem' }}
              />
            </div>
            {error && <FeedbackMessage ok={false} msg={error} />}
          </div>
          <footer className="bgFootBar" style={{ borderRadius: 0, borderInline: 0, borderBottom: 0 }}>
            <span className="bgFootStatus">The code is case-insensitive; the PIN must be exact.</span>
            <div className="bgFootActions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={joinWords.trim().length === 0 || pin.trim().length !== 6}
                onClick={() => { setError(null); setStage('auth'); }}
              >
                Continue
              </button>
            </div>
          </footer>
        </div>
      )}

      {stage === 'auth' && (
        <div className="bgPanel">
          <div className="bgPanelBody">
            <h2 style={{ fontSize: 'var(--size-h3)', color: 'var(--color-text)' }}>
              Verify your identity
            </h2>
            <p style={{ color: 'var(--color-text2)' }}>
              Your identity is attested to your key so the initiator can confirm it’s really you.
            </p>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={signInOidc}>
              {busy ? <span className="spinner" aria-hidden="true" /> : 'Sign in with your organization'}
            </button>
            <div className="bgJoinMeta">— or, external guest —</div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{ flex: 1, minWidth: '14rem' }}
              />
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy || resolvedSessionId == null && magicToken == null}
                onClick={() =>
                  resolvedSessionId && magicToken
                    ? consumeMagicLink(resolvedSessionId, magicToken)
                    : sendMagicLink()
                }
              >
                {magicToken ? 'Verify magic link' : 'Send magic link'}
              </button>
            </div>
            {error && <FeedbackMessage ok={false} msg={error} />}
          </div>
        </div>
      )}

      {stage === 'keychoice' && (
        <div className="bgPanel">
          <div className="bgPanelBody">
            <h2 style={{ fontSize: 'var(--size-h3)', color: 'var(--color-text)' }}>
              Choose how to hold your key
            </h2>
            <div className="bgChips" role="group" aria-label="Key custody">
              <button
                type="button"
                className={`bgChip ${hold === 'software' ? 'bgChipActive' : ''}`}
                aria-pressed={hold === 'software'}
                onClick={() => setHold('software')}
              >
                This device (software key)
              </button>
              <button
                type="button"
                className={`bgChip ${hold === 'hardware' ? 'bgChipActive' : ''}`}
                aria-pressed={hold === 'hardware'}
                disabled={caps.webAuthnPrf.level === 'unsupported'}
                onClick={() => setHold('hardware')}
              >
                Security key (hardware)
              </button>
            </div>
            <p className="bgShieldNote" style={{ marginTop: 0 }}>
              Your private key is created on this device and never leaves it.
            </p>
            {prfNote && hold === 'hardware' && <FeedbackMessage ok msg={prfNote} />}
            {error && <FeedbackMessage ok={false} msg={error} />}
          </div>
          <footer className="bgFootBar" style={{ borderRadius: 0, borderInline: 0, borderBottom: 0 }}>
            <span className="bgFootStatus">
              <Icon icon={ShieldCheck} size="xs" tone="accent" /> Generated and held in your browser.
            </span>
            <div className="bgFootActions">
              <button type="button" className="btn btn-primary" disabled={busy} onClick={generateAndPublish}>
                Generate key
              </button>
            </div>
          </footer>
        </div>
      )}

      {stage === 'generating' && (
        <div className="bgPanel">
          <div className="bgPanelBody" role="status" aria-live="polite">
            <h2 style={{ fontSize: 'var(--size-h3)', color: 'var(--color-text)' }}>{genLabel}</h2>
            <span className="spinner" aria-hidden="true" style={{ fontSize: '1.5rem' }} />
          </div>
        </div>
      )}

      {stage === 'waiting' && (
        <div className="bgPanel">
          <div className="bgPanelBody">
            <h2 style={{ fontSize: 'var(--size-h3)', color: 'var(--color-text)' }}>
              You’re in the lobby
            </h2>
            <p style={{ color: 'var(--color-text2)' }}>
              Public key published. Read your safety number to the initiator out of band so they
              can confirm it’s really you:
            </p>
            {fingerprint && <CodeBlock code={fingerprint.words.join(' · ')} />}
            <div className="bgLive" role="status" aria-live="polite">
              <span className="bgLiveDot" aria-hidden="true" />
              Waiting for the initiator to admit you…
            </div>
            <p className="bgShieldNote" style={{ marginTop: 0 }}>
              When you’re admitted and the secret is sealed, you’ll custody one shard. The server
              can’t read it; only you can.
            </p>
          </div>
          <footer className="bgFootBar" style={{ borderRadius: 0, borderInline: 0, borderBottom: 0 }}>
            <span className="bgFootStatus">Signed in as {auth?.email} · {auth?.method}</span>
            <div className="bgFootActions">
              <button type="button" className="btn btn-ghost" onClick={() => navigate('/')}>
                Done
              </button>
            </div>
          </footer>
        </div>
      )}
    </div>
  );
}

export default ReceiverFlow;
